import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import {
  getPage,
  getAnnouncementBanner,
  getStackedLogos,
  type Page as SanityPage,
  type Banner,
  type Customer,
} from "lib/sanity";
import { Page } from "components/page";
import { SimpleTextHero } from "components/hero";
import { AboutHightouch } from "components/about-hightouch";
import { StackedLogos } from "components/logos";
import { Values } from "components/values";
import { getPageMetaImage } from "utils";
import { Button } from "ui/button";
import { IndustryLeaderAwards } from "components/content";
import { sectionSpacingY } from "components/section";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

const About: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, customerRows }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      footerOptions={{
        ctaProps: {
          title: "Join the data activation movement",
          description: "",
          actions: (
            <Button hasRightChevron href="/careers">
              Explore open positions
            </Button>
          ),
        },
      }}
    >
      <SimpleTextHero title={page.title} description={page.description} />

      <AboutHightouch />

      <StackedLogos
        customerRows={customerRows}
        title="We're proud to serve the most amazing companies out there"
        titleMaxW="4xl"
      />

      <Values />

      <IndustryLeaderAwards hideBg my="0" py={sectionSpacingY} />
    </Page>
  );
};

export default About;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, customerRows] = await Promise.all([
    getPage("/about"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
